/**
 * Messages between `MusicPlayer` (main thread) and the SID AudioWorklet processor (spec 07 §4), and
 * the processor's name. Pure: imported by both sides.
 */

import type { EffectLane } from '../sfx/effectNames';
import type { SidQuality } from '../sid/sidChip';

/** Name the processor registers under. */
export const SID_PROCESSOR = 'sid-music';

/** `processorOptions` of the AudioWorkletNode. */
export interface SidProcessorOptions {
  clockHz: number;
  frameHz: number;
  quality: SidQuality;
}

export type ToWorklet =
  /**
   * Start a subtune from its beginning; `id` comes back in `ended`. `effects`: an in-game tune, during
   * which sound effects may play on voice 1 (the C64 plays them only under subtunes 1, 2, 3 and 6).
   */
  | {
    type: 'play'; id: number; init: readonly number[]; frames: readonly (readonly number[])[]; loopFrame: number;
    effects?: boolean;
  }
  /** Stop the music: its voices release quickly; an effect playing stops with it. */
  | { type: 'stop' }
  /** Switch the renderer, through a few ms of fade so nothing clicks. */
  | { type: 'quality'; quality: SidQuality }
  /** Music volume 0..1 (voices 2 and 3, and voice 1 while the tune has it), ramped. */
  | { type: 'volume'; volume: number }
  /** Effects volume 0..1 (voice 1 while the effects have it), ramped. */
  | { type: 'effectsVolume'; volume: number }
  /** Ask for a captured effect (`SfxPlayer`): per 50 Hz frame, flat [register 0..6, value, ...]. */
  | { type: 'effect'; name: string; lane: EffectLane; frames: readonly (readonly number[])[]; waits?: boolean };

export type FromWorklet =
  /** An ending tune (a jingle) passed its last frame. */
  { type: 'ended'; id: number };

const TYPES: readonly string[] = ['play', 'stop', 'quality', 'volume', 'effectsVolume', 'effect'];

/** Shape check for data arriving over the MessagePort. */
export function isToWorklet(data: unknown): data is ToWorklet {
  return typeof data === 'object' && data !== null && 'type' in data && TYPES.includes(String(data.type));
}
